import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listStockoutAlerts, updateStockoutAlert } from "@/lib/forecasting.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Eye } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/forecasting/alerts")({ component: AlertsPage });

type Status = "open" | "acknowledged" | "resolved";

function sevTone(s: string) {
  if (s === "high") return "destructive" as const;
  if (s === "medium") return "secondary" as const;
  return "outline" as const;
}

function AlertsPage() {
  const { tenantId, facilityId, customerId } = useScope();
  const listFn = useServerFn(listStockoutAlerts);
  const updateFn = useServerFn(updateStockoutAlert);
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>("open");

  const { data, isLoading } = useQuery({
    queryKey: ["stockout-alerts", tenantId, facilityId, customerId, status],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null, status } }),
  });

  const change = useMutation({
    mutationFn: (vars: { id: string; status: "acknowledged" | "resolved" }) =>
      updateFn({ data: { id: vars.id, tenantId: tenantId!, status: vars.status } }),
    onSuccess: () => {
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["stockout-alerts"] });
      qc.invalidateQueries({ queryKey: ["forecast-overview"] });
    },
  });

  const rows = data?.rows ?? [];

  return (
    <>
      <PageHeader title="Stockout Alerts" description="SKUs projected to run out within their lead time, ranked by severity." />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <Tabs value={status} onValueChange={(v) => setStatus(v as Status)}>
          <TabsList>
            <TabsTrigger value="open">Open</TabsTrigger>
            <TabsTrigger value="acknowledged">Acknowledged</TabsTrigger>
            <TabsTrigger value="resolved">Resolved</TabsTrigger>
          </TabsList>
        </Tabs>
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead className="text-right">On hand</TableHead>
                    <TableHead className="text-right">Daily demand</TableHead>
                    <TableHead className="text-right">Days of cover</TableHead>
                    <TableHead>Projected stockout</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
                  {!isLoading && rows.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No {status} alerts.</TableCell></TableRow>
                  )}
                  {rows.map((a: any) => {
                    const sku = a.skus as { sku_code: string; name: string } | null;
                    return (
                      <TableRow key={a.id}>
                        <TableCell>
                          <div className="font-mono text-xs">{sku?.sku_code ?? a.sku_id.slice(0, 8)}</div>
                          <div className="text-xs text-muted-foreground">{sku?.name ?? ""}</div>
                        </TableCell>
                        <TableCell><Badge variant={sevTone(a.severity)} className="capitalize">{a.severity}</Badge></TableCell>
                        <TableCell className="text-right tabular-nums">{a.on_hand_qty}</TableCell>
                        <TableCell className="text-right tabular-nums">{Number(a.daily_demand).toFixed(1)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{Number(a.days_of_cover).toFixed(1)}</TableCell>
                        <TableCell className="text-sm">{a.projected_stockout_date ?? "—"}</TableCell>
                        <TableCell className="text-right">
                          {status === "open" && (
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" onClick={() => change.mutate({ id: a.id, status: "acknowledged" })}><Eye className="h-3.5 w-3.5" /></Button>
                              <Button size="sm" variant="outline" onClick={() => change.mutate({ id: a.id, status: "resolved" })}><CheckCircle2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          )}
                          {status === "acknowledged" && (
                            <Button size="sm" variant="outline" onClick={() => change.mutate({ id: a.id, status: "resolved" })}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Resolve</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}